<template>
  <mdb-row class="mt-3">
    <mdb-col col="9">
      <div class="d-flex flex-row justify-content-center">
        <h5>Relations</h5>
        <mdb-btn
          v-if="isAdmin"
          class="mt-0"
          :disabled="readOnly || !nodeUuid"
          rounded
          size="sm"
          icon="plus"
          gradient="aqua"
          @click.native="createRelation()"
          title="Create new relationship for selected node"
        />
      </div>
      <mdb-container>
        <mdb-row>
          <mdb-col col="7" class="text-left"><h6>Types</h6></mdb-col>
          <mdb-col col="5" class="text-left pl-1"><h6>Nodes</h6></mdb-col>
        </mdb-row>
      </mdb-container>
      <mdb-container class="admin-tab-node-relationships-body border">
        <mdb-row class="h-100">
          <mdb-col col="7" class="h-100 pr-1">
            <bridge8-select-list
              class="pb-2 pt-2 h-100"
              entity="rel type"
              :search="true"
              :value="relType"
              :options="relTypes"
              :text-callback="formatRelTypeText"
              @selected="relationshipTypeSelected($event)"
            />
          </mdb-col>
          <mdb-col col="5" class="h-100 pl-1 pr-1">
            <admin-tab-node-relationships-nodes
              :rels-by-label="rels"
              :read-only="readOnly"
              @select="relationshipSelected($event)"
              @delete-relationship="deleteNodeRelationship($event)"
            />
          </mdb-col>
        </mdb-row>
      </mdb-container>
      <mdb-container class="admin-tab-node-relationships-body mt-3">
        <mdb-row class="h-100">
          <admin-tab-entity-properties
            class="h-100 w-100"
            :read-only="readOnly && !relNode"
            :initialProperties="relProperties"
            @props-change="propertiesChanged($event)"
          />
        </mdb-row>
      </mdb-container>
    </mdb-col>
    <mdb-col col="3">
      <div class="d-flex flex-column align-items-center justify-content-center">
        <h5>Related Node</h5>
        <mdb-btn
          v-if="isAdmin"
          class="mt-0"
          :disabled="readOnly || !relNode"
          rounded
          size="sm"
          gradient="aqua"
          icon="list-alt"
          @click.native="changeNode()"
          title="Change related node for selected relationship"
        />
      </div>
      <div class="d-flex flex-column pb-2 pt-2 admin-tab-related-node-info">
        <mdb-tbl class="admin-tab-rel-node-info-tbl">
          <mdb-tbl-body>
            <tr
              v-for="(value, key) in getTableValues(relNode)"
              :key="`popover-table-row-${key}`"
            >
              <td class="cell cell-key text-right pr-1 pt-1 pb-1">{{ key }}:</td>
              <td class="cell cell-value text-left pl-1 pt-1 pb-1">
                <a v-if="key === 'email'" :href="`mailto:${value}`">{{ value }}</a>
                <a v-else-if="key === 'url'" :href="value">{{ value }}</a>
                <span v-else>{{ value }}</span>
              </td>
            </tr>
          </mdb-tbl-body>
        </mdb-tbl>
      </div>
    </mdb-col>
  </mdb-row>
</template>

<script>
import {mdbContainer, mdbRow, mdbCol, mdbTbl, mdbTblBody, mdbBtn, mdbIcon} from 'mdbvue'
import {RelationshpDirections} from '../enums'
import Bridge8SelectList from './Bridge8SelectList'
import AdminTabNodeRelationshipsNodes from './AdminTabNodeRelationshipsNodes'
import {errorCatch, tableValues} from '../utils'
import {mapGetters, mapActions} from 'vuex'
import AdminTabEntityProperties from './AdminTabEntityProperties'

export default {
  name: 'AdminTabNodeRelationships',
  data () {
    return {
      relTypes: [],
      relType: null,
      relTypeWatch: true,
      rels: [],
      relNode: null,
      relUuid: null,
      relProperties: []
    }
  },
  props: {
    readOnly: {
      type: Boolean,
      default: true
    },
    nodeUuid: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapGetters('user', ['isAdmin'])
  },
  methods: {
    ...mapActions([
      'apiNodeGetRelationshipTypes',
      'apiNodeGetRelatedNodes',
      'apiRelationshipDelete',
      'apiRelationshipCreate',
      'apiRelationshipGetProperties',
      'apiRelationshipSetProperties',
      'apiRelationshipSetNodes'
    ]),
    formatRelTypeText (rel) {
      let arrow
      switch (rel.direction) {
        case RelationshpDirections.INCOMING:
          arrow = '<<'
          break
        case RelationshpDirections.OUTGOING:
          arrow = '>>'
          break
        default:
          throw new Error('Unknown Relationship Direction')
      }
      return `${rel.type} ${arrow}`
    },
    getNodeRelationTypes () {
      this.rels = []
      if (this.nodeUuid === null) {
        this.relTypes = []
      } else {
        return this.apiNodeGetRelationshipTypes(this.nodeUuid).then(relTypes => {
          this.relTypes = relTypes
        }).catch(errorCatch('Get Node Properties Failed.', this))
      }
    },
    getNodeRelatedNodes (relType = null) {
      if (relType) {
        this.relTypeWatch = false
        this.relType = relType
        this.relTypeWatch = true
      }
      if (this.nodeUuid === null || this.relType === null) {
        this.rels = []
      } else {
        return this.apiNodeGetRelatedNodes({uuid: this.nodeUuid, relType: this.relType})
          .then(relNodes => {
            this.rels = relNodes
          }).catch(errorCatch('Get Node Related Nodes Failed.', this))
      }
    },
    deleteNodeRelationship ({uuid, isLast}) {
      this.apiRelationshipDelete(uuid).then(() => {
        if (isLast) {
          this.getNodeRelationTypes()
        } else {
          this.getNodeRelatedNodes()
        }
      }).catch(errorCatch('Delete Node Relationship Failed', this))
    },
    getRelationshipProperties () {
      this.apiRelationshipGetProperties(this.relUuid).then(props => { this.relProperties = props })
        .catch(errorCatch('Get Relationship Properties Failed', this))
    },
    relationshipSelected (params) {
      if (params === null) {
        this.relUuid = null
        this.relNode = null
      } else {
        this.relUuid = params.uuid
        this.relNode = params.node
      }
    },
    relationshipTypeSelected (relType) {
      this.relType = relType
    },
    propertiesChanged (props) {
      const propsData = {}
      props.forEach(prop => {
        propsData[prop.key] = prop.value
      })
      // TODO: Check why it fires several times
      return this.apiRelationshipSetProperties({ uuid: this.relUuid, props: propsData })
        .then(() => {
          this.getRelationshipProperties()
        })
        .catch(errorCatch('Relationship Properties Change failed.', this))
    },
    changeNode () {
      this.$bus.$emit('admin-modal-select-node', node => {
        let from = null
        let to = null
        switch (this.relType.direction) {
          case RelationshpDirections.INCOMING:
            from = node.uuid
            to = this.nodeUuid
            break
          case RelationshpDirections.OUTGOING:
            from = this.nodeUuid
            to = node.uuid
            break
          default:
            throw new Error('Unknown Relationship Direction')
        }
        this.apiRelationshipSetNodes({uuid: this.relUuid, from: from, to: to}).then(uuid => {
          this.getNodeRelatedNodes().then(() => {
            // this.$bus.$emit('admin-select-related-node-by-rel-uuid', uuid)
          })
        })
      })
    },
    createRelation () {
      this.$bus.$emit('admin-modal-select-rel-type', ({type, direction}) => {
        this.$bus.$emit('admin-modal-select-node', node => {
          let from = null
          let to = null
          switch (direction) {
            case RelationshpDirections.INCOMING:
              from = node.uuid
              to = this.nodeUuid
              break
            case RelationshpDirections.OUTGOING:
              from = this.nodeUuid
              to = node.uuid
              break
            default:
              throw new Error('Unknown Relationship Direction')
          }
          this.apiRelationshipCreate({type: type, from: from, to: to}).then(uuid => {
            this.getNodeRelationTypes()
              .then(() => {
                this.getNodeRelatedNodes({type: type, direction: direction}).then(() => {
                  // this.$bus.$emit('admin-select-related-node-by-rel-uuid', uuid)
                })
              })
          })
        })
      })
    },
    getTableValues (obj) {
      return tableValues(obj, true)
    }
  },
  watch: {
    nodeUuid (newVal, oldVal) {
      this.getNodeRelationTypes()
    },
    relType (newVal, oldVal) {
      if (this.relTypeWatch) {
        this.getNodeRelatedNodes()
      }
    },
    relNode (newVal, oldVal) {
      if (newVal === null) {
        this.relProperties = []
        return
      }
      if (newVal !== oldVal) {
        this.getRelationshipProperties()
      }
    }
  },
  components: {
    AdminTabEntityProperties,
    Bridge8SelectList,
    AdminTabNodeRelationshipsNodes,
    mdbContainer,
    mdbRow,
    mdbCol,
    mdbTbl,
    mdbTblBody,
    mdbBtn,
    mdbIcon
  }
}
</script>

<style lang="sass">
.admin-tab-node-relationships-body
  height: 20vh
  overflow: hidden

.admin-tab-related-node-info
  overflow-x: scroll

.input-readonly
  opacity: 0.8

.type-select
  input
    font-size: 1em!important

.cell
  font-size: small!important

  &.cell-value
    white-space: nowrap
</style>



// WEBPACK FOOTER //
// src/components/AdminTabNodeRelationships.vue